import React, { lazy } from 'react';
// all stylesheets, eagerly and in a fixed order — must stay the first import (see styles.js)
import './styles';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from 'views/base/Layout.js';
import BareLayout from 'views/base/BareLayout.js';
import { captureReferralCode } from 'helpers/referral';
import { ColorModeProvider } from 'context/ColorModeContext';

// Route components are split out of the main bundle and fetched when their
// route is first shown (Layout wraps the outlet in a Suspense boundary). The
// shell — Layout, the nav, the footer, the consent banner — stays in main.
const Info = lazy(() => import('views/base/Info'));
const Home = lazy(() => import('views/home/Home'));
const Enterprise = lazy(() => import('views/enterprise/Enterprise'));
const Partners = lazy(() => import('views/partners/Partners'));
const Blogs = lazy(() => import('views/blogs/Blogs'));
const BlogDetails = lazy(() => import('views/blogs/BlogDetails'));
const ContactUs = lazy(() => import('views/contactUs/ContactUs'));
const TermsAndConditions = lazy(() => import('views/resources/TermsAndConditions'));
const Pricing = lazy(() => import('views/pricing/Pricing'));
const Brandbook = lazy(() => import('views/brandbook/Brandbook'));
const About = lazy(() => import('views/about/About'));
const ChromeExtension = lazy(() => import('views/chromeExtension/ChromeExtension'));
const Career = lazy(() => import('views/career/Career'));
const UseCases = lazy(() => import('views/useCases/UseCases'));
const Landing = lazy(() => import('views/landing/Landing'));
const SolutionSales = lazy(() => import('views/solution/SolutionSales'));
const SolutionMarketing = lazy(() => import('views/solution/SolutionMarketing'));
const SolutionLegal = lazy(() => import('views/solution/SolutionLegal'));
const SolutionSupport = lazy(() => import('views/solution/SolutionSupport'));
const SolutionHR = lazy(() => import('views/solution/SolutionHR'));
const SolutionFinance = lazy(() => import('views/solution/SolutionFinance'));
const SolutionEngineering = lazy(() => import('views/solution/SolutionEngineering'));
const AIAssistant = lazy(() => import('views/products/AIAssistant'));
const AIBot = lazy(() => import('views/products/AIBot'));
const MonetizeAI = lazy(() => import('views/products/MonetizeAI'));
const Refer = lazy(() => import('views/referral/Refer'));
const PhoneAgent = lazy(() => import('views/phoneAgent/PhoneAgent'));

// Referral code off the URL, at module scope so it runs on EVERY entry into
// the app — before React mounts and before any route renders.
//
// It was previously an effect inside Layout. That covered all 29 routes, since
// every one of them nests under Layout, so it worked — but it made capture
// depend on a component mounting successfully, and it ran after the first
// render rather than before it. A redirect or a throw in a page component
// during that window would take the code with it.
//
// Doing it here matches the portal (App.js), where the equivalent effect had
// been scoped to the sign-up page and silently lost attribution for anyone who
// arrived on any other URL first — which is the normal path through a referral
// link, since links point at the site root.
captureReferralCode();

function ExternalRedirect({ to }) {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

// An in-app redirect that keeps the query string and hash, so a campaign
// link to an old path (utm_* and all) lands on the new one intact.
function Redirect({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ColorModeProvider>
    <BrowserRouter>
      <Routes>
          <Route path="/billing/balance" element={<ExternalRedirect to="/portal/billing/subscribe" />} />
          {/* campaign pages: served without the site's header and footer */}
          <Route element={<BareLayout />}>
            <Route path="/phone-agent" element={<PhoneAgent />} />
          </Route>
          <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="/enterprise" element={<Enterprise />} />
          {/* the enterprise page used to live at /business; links out there still do */}
          <Route path="/business" element={<Redirect to="/enterprise" />} />
          <Route path="/partners" element={<Partners />} />
          <Route path='/pricing' element={<Pricing/>} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/page/:id" element={<Blogs />} />
          <Route path="/blogs/details/:id" element={<BlogDetails />} />
          <Route path="/blogs/:categories" element={<Blogs />} />
          <Route path="/blogs/:categories/:pageId" element={<Blogs />} />
          <Route path='/contact-us' element={<ContactUs/>} />
          <Route path='/terms-and-conditions' element={<TermsAndConditions/>} />
          <Route path='/brandbook' element={<Brandbook/>} />
          <Route path='/about' element={<About/>} />
          <Route path='/chrome-extension' element={<ChromeExtension/>} />
          <Route path='/careers' element={<Career/>}/>
          <Route path='/features' element={<UseCases/>}/>
          <Route path='/landing' element={<Landing/>}/>
          <Route path='/solution-sales' element={<SolutionSales/>}/>
          <Route path='/solution-marketing' element={<SolutionMarketing/>}/>
          <Route path='/solution-legal' element={<SolutionLegal/>}/>
          <Route path='/solution-support' element={<SolutionSupport/>}/>
          <Route path='/solution-hr' element={<SolutionHR/>}/>
          <Route path='/solution-finance' element={<SolutionFinance/>}/>
          <Route path='/solution-engineering' element={<SolutionEngineering/>}/>
          <Route path="/products/ai-assistant" element={<AIAssistant/>} />
          <Route path='/products/ai-bot' element={<AIBot/>}/>
          <Route path='/products/ai-monetize' element={<MonetizeAI/>}/>
          {/* Where a shared referral link lands. The code is a path segment,
              not a query parameter, so the module-scope capture above does not
              see it — ReferInvite remembers it itself on first render. */}
          <Route path='/refer/:code' element={<Refer/>}/>
          <Route path="*" element={<Info status={404}/>} />
          </Route>
      </Routes>
    </BrowserRouter>
    </ColorModeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
